import { useState, useEffect } from 'react'
import { Modal } from '../ui/Modal'
import type { Site } from '../../types'

interface Props {
  open: boolean
  site: Site | null
  onClose: () => void
  onSave: (id: string, updates: Partial<Pick<Site, 'name' | 'group_name'>>) => Promise<void>
}

export function EditSiteDialog({ open, site, onClose, onSave }: Props) {
  const [name, setName] = useState('')
  const [group, setGroup] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (site) {
      setName(site.name)
      setGroup(site.group_name ?? '')
      setError(null)
    }
  }, [site])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!site) return

    setSaving(true)
    setError(null)
    try {
      await onSave(site.id, {
        name: name.trim() || site.name,
        group_name: group.trim() || null,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'
  const labelCls = 'block text-xs uppercase tracking-wide text-gray-500 mb-1.5'

  return (
    <Modal open={open} onClose={onClose} title={`Edit — ${site?.name ?? 'Site'}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelCls}>Site Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls}
            autoFocus
          />
        </div>

        <div>
          <label className={labelCls}>Group</label>
          <input
            type="text"
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            placeholder="Optional — for organizing sites"
            className={inputCls}
          />
        </div>

        {error && (
          <p className="text-error text-sm">{error}</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
